import React, { useState, useEffect } from 'react';
import { 
  FiEye,         // View icon
  FiMessageCircle, // Message/Contact icon
  FiPhone,       // Phone icon
  FiRefreshCw    // Refresh icon
} from 'react-icons/fi';
import { IoSearchOutline } from 'react-icons/io5';
import { FaWhatsapp } from 'react-icons/fa';
const StudentCallbacks = () => {
  const [callbacks, setCallbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const fetchData = async (signal) => {
    setLoading(true);
    try {
      // Fetch callbacks data from the API based on filter
      let callbacksUrl = 'https://apissc.sainikschoolcadet.com/api/student-callback';
      
      // If filter is not 'all', use the type endpoint
      if (filter !== 'all') {
        callbacksUrl = `https://apissc.sainikschoolcadet.com/api/student-callback/type/${filter}`;
      }
      
      const callbacksResponse = await fetch(callbacksUrl, { signal });
      if (!callbacksResponse.ok) {
        throw new Error('Failed to fetch callbacks');
      }
      const callbacksData = await callbacksResponse.json();
      
      // Fetch student details for each callback
      const callbacksWithStudentDetails = await Promise.all(
        callbacksData.map(async (callback) => {
          try {
            const studentResponse = await fetch(`https://apissc.sainikschoolcadet.com/api/students/getbyid/${callback.s_id}`, { signal });
            if (!studentResponse.ok) {
              throw new Error(`Failed to fetch student with ID ${callback.s_id}`);
            }
            const studentData = await studentResponse.json();
            
            return {
              ...callback,
              student_name: studentData.f_name || 'Unknown',
              phone: studentData.phonenumber || 'N/A',
              email: studentData.email || 'N/A'
            };
          } catch (studentError) {
            if (studentError.name === 'AbortError') throw studentError;
            console.error(`Error fetching student ${callback.s_id}:`, studentError);
            return {
              ...callback,
              student_name: `Student ${callback.s_id}`,
              phone: 'N/A',
              email: 'N/A'
            };
          }
        })
      );
      
      if (signal && signal.aborted) return;

      setCallbacks(callbacksWithStudentDetails);
      setLoading(false);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error("Error fetching data:", err);
      setError(err.message);
      setLoading(false);
    }
  };
  
  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);

    return () => {
      controller.abort();
    };
  }, [filter]); // Re-fetch when filter changes, aborting in-flight requests
  
  // Calculate summary statistics
  const totalCallbacks = callbacks.length;
  const onlineCallbacks = callbacks.filter(cb => cb.preferred_mode === "online").length;
  const offlineCallbacks = callbacks.filter(cb => cb.preferred_mode === "offline").length;
  
  // Format date for display
  const formatDate = (dateString) => {
    const options = { day: 'numeric', month: 'short', year: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  // Enhanced date formatting function with long format
const formattimeDate = (dateString) => {
  const date = new Date(dateString);
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return 'Invalid date';
  }
  
  // Get date components
  const day = date.getDate();
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear();
  
  // Get time components
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  
  // Format: 5 May 2025 15:23
  return `${day} ${month} ${year} ${hours}:${minutes}`;
};
  // Handle refresh button click
  const handleRefresh = () => {
    fetchData();
  };
  
  // Handle view student details
  const handleViewStudent = (studentId) => {
    window.open(`/view-student/${studentId}`, '_blank', 'noopener,noreferrer');
  };
  
  // Handle contact action
  const handleContact = (phone, mode) => {
    if (mode === 'online') {
      // Format phone number for WhatsApp
      const formattedNumber = phone.startsWith('+91') 
        ? phone 
        : `+91${phone.replace(/[^\d]/g, '')}`;
      
      // Open WhatsApp web with the formatted number
      window.open(`https://wa.me/${formattedNumber}`, '_blank', 'noopener,noreferrer');
    } else {
      // For calling, we can just create a tel: link
      window.open(`tel:${phone}`, '_blank', 'noopener,noreferrer');
    }
  };
  
  if (error) {
    return (
      <div className="p-4 max-w-full mx-auto">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-2xl shadow-md p-6">
      {/* Header Section */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">Student Callbacks</h2>
        <button 
          onClick={handleRefresh}
          className="text-gray-600 hover:text-blue-600 transition-colors"
          title="Refresh"
        >
          <FiRefreshCw size={20} />
        </button>
      </div>
      
      {/* Stats Section */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-2">Total Callbacks</p>
          <p className="text-2xl font-bold text-blue-600">{totalCallbacks}</p>
        </div>
        
        <div className="bg-green-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-2">Online Callbacks</p>
          <p className="text-2xl font-bold text-green-600">
            {onlineCallbacks}
            <span className="text-sm text-gray-500 ml-1">
              {totalCallbacks > 0 ? ` (${Math.round((onlineCallbacks/totalCallbacks)*100)}%)` : ' (0%)'}
            </span>
          </p>
        </div>
        
        <div className="bg-purple-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-2">Offline Callbacks</p>
          <p className="text-2xl font-bold text-purple-600">
            {offlineCallbacks}
            <span className="text-sm text-gray-500 ml-1">
              {totalCallbacks > 0 ? ` (${Math.round((offlineCallbacks/totalCallbacks)*100)}%)` : ' (0%)'}
            </span>
          </p>
        </div>
      </div>
      
      {/* Filters and Search */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border border-gray-300 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="all">All Modes</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
          </select>
          <div className="relative">
            <input 
              type="text"
              placeholder="Search Student"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border border-gray-300 px-3 py-2 rounded-lg pl-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            <IoSearchOutline className="h-5 w-5 absolute left-2 top-3 text-gray-400" />
          </div>
        </div>
      </div>
      
      {/* Filter callbacks based on search query */}
      {(() => {
        const filteredCallbacks = callbacks.filter(callback => 
          callback.student_name.toLowerCase().includes(searchQuery.toLowerCase())
        );
        
        return (
          <>
            {/* Loading State */}
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-blue-500"></div>
              </div>
            ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S.NO.</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Student Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Preferred Mode
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Start Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created At
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCallbacks.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-4 text-center text-gray-500">
                    No callbacks found
                  </td>
                </tr>
              ) : (
                filteredCallbacks.map((callback, index) => (
                  <tr key={callback.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {index + 1}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          {callback.student_name ? callback.student_name.charAt(0).toUpperCase() : '?'}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{callback.student_name}</div>
                          <div className="text-sm text-gray-500">{callback.phone}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        callback.preferred_mode === 'online' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {callback.preferred_mode}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(callback.start_date)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                     {formattimeDate(callback.created_at)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewStudent(callback.s_id)}
                          className="text-blue-500 hover:text-blue-700 p-1 rounded-full hover:bg-blue-50"
                          title="View Student"
                        >
                          <FiEye size={18} />
                        </button>
                        <button
  onClick={() => handleContact(callback.phone, callback.preferred_mode)}
  className={`p-1 rounded-full ${
    callback.preferred_mode === 'online' 
      ? 'text-green-500 hover:text-green-700 hover:bg-green-50' 
      : 'text-purple-500 hover:text-purple-700 hover:bg-purple-50'
  }`}
  title={callback.preferred_mode === 'online' ? 'Contact on WhatsApp' : 'Call Student'}
>
  {callback.preferred_mode === 'online' 
    ? <FaWhatsapp size={18} /> 
    : <FiPhone size={18} />}
</button>

                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
          </>
        );
      })()}
      
      {/* Pagination */}
      {callbacks.filter(callback => 
        callback.student_name.toLowerCase().includes(searchQuery.toLowerCase())
      ).length > 0 && (
        <div className="flex items-center justify-between mt-4">
          <div>
            <p className="text-sm text-gray-700">
              Showing 1 to {callbacks.filter(callback => 
                callback.student_name.toLowerCase().includes(searchQuery.toLowerCase())
              ).length} of {callbacks.filter(callback => 
                callback.student_name.toLowerCase().includes(searchQuery.toLowerCase())
              ).length} results
            </p>
          </div>
          <div className="flex space-x-1">
            <button className="px-3 py-1 rounded border border-gray-300 bg-white text-gray-500 hover:bg-gray-50">
              &lt;
            </button>
            <button className="px-3 py-1 rounded border border-blue-500 bg-blue-50 text-blue-600">
              1
            </button>
            <button className="px-3 py-1 rounded border border-gray-300 bg-white text-gray-500 hover:bg-gray-50">
              &gt;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentCallbacks;